// @ts-nocheck
/**
 * usePaginatedMessages — cursor-based message loading with reverse-scroll support.
 *
 * Initial load fetches the most recent page (or anchors around lastReadAt).
 * Exposes `loadOlder()` for prepending history when the user scrolls up,
 * and polls for new messages via `after` cursor.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { apiRequest } from "@/lib/query-client";

interface PaginatedMessage {
  id: string;
  createdAt: string;
  [key: string]: any;
}

interface UsePaginatedMessagesOpts {
  spaceId: string;
  enabled?: boolean;
  /** ISO timestamp of the user's last-read position (from participant record) */
  lastReadAt?: string | null;
  /** Poll interval in ms for new messages (0 = no polling) */
  pollInterval?: number;
  /** Page size */
  pageSize?: number;
}

interface UsePaginatedMessagesReturn {
  messages: PaginatedMessage[];
  hasOlder: boolean;
  isLoadingInitial: boolean;
  isLoadingOlder: boolean;
  loadOlder: () => Promise<void>;
  /** Index in the messages array where unread messages start (null if all read) */
  unreadAnchorIndex: number | null;
  /** Append a confirmed message (used for optimistic send updates) */
  appendMessage: (msg: PaginatedMessage) => void;
  /** Update a message in-place (used for optimistic action status changes) */
  updateMessage: (id: string, updater: (msg: PaginatedMessage) => PaginatedMessage) => void;
  /** Immediately poll for new messages (call on SSE new-message event) */
  pollNow: () => Promise<void>;
}

const PAGE_SIZE = 30;

export function usePaginatedMessages({
  spaceId,
  enabled = true,
  lastReadAt,
  pollInterval = 5000,
  pageSize = PAGE_SIZE,
}: UsePaginatedMessagesOpts): UsePaginatedMessagesReturn {
  const [messages, setMessages] = useState<PaginatedMessage[]>([]);
  const [hasOlder, setHasOlder] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [unreadAnchorIndex, setUnreadAnchorIndex] = useState<number | null>(null);

  // Track whether initial load has completed to avoid duplicate fetches
  const initialLoadDone = useRef(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const spaceIdRef = useRef(spaceId);
  // Guard against race conditions when spaceId changes
  const loadGenRef = useRef(0);

  // Reset when spaceId changes
  useEffect(() => {
    spaceIdRef.current = spaceId;
    initialLoadDone.current = false;
    loadGenRef.current++;
    setMessages([]);
    setHasOlder(false);
    setUnreadAnchorIndex(null);
  }, [spaceId]);

  const buildUrl = useCallback(
    (params: Record<string, string | number>) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
      }
      return `/api/chat/spaces/${encodeURIComponent(spaceId)}/messages?${qs}`;
    },
    [spaceId],
  );

  // --- Initial load ---
  useEffect(() => {
    if (!enabled || initialLoadDone.current) return;

    const gen = ++loadGenRef.current;

    (async () => {
      setIsLoadingInitial(true);
      try {
        const params: Record<string, string | number> = { limit: pageSize };
        // If there's a lastReadAt and it's not "never read" (null), anchor around it
        // so unread messages appear below the fold.
        if (lastReadAt) {
          params.around_ts = lastReadAt;
        }
        const res = await apiRequest("GET", buildUrl(params));
        const data = await res.json();

        if (gen !== loadGenRef.current) return; // stale

        const msgs: PaginatedMessage[] = data.messages ?? data;
        setMessages(msgs);
        setHasOlder(data.hasOlder ?? false);

        // Compute unread anchor
        if (lastReadAt && msgs.length > 0) {
          const readTs = new Date(lastReadAt).getTime();
          const idx = msgs.findIndex(
            (m) => new Date(m.createdAt).getTime() > readTs,
          );
          setUnreadAnchorIndex(idx >= 0 ? idx : null);
        } else {
          setUnreadAnchorIndex(null);
        }

        initialLoadDone.current = true;
      } catch (err) {
        console.error("[usePaginatedMessages] initial load failed:", err);
      } finally {
        if (gen === loadGenRef.current) setIsLoadingInitial(false);
      }
    })();
  }, [enabled, spaceId, lastReadAt, pageSize, buildUrl]);

  // --- Load older (scroll-up) ---
  const loadOlder = useCallback(async () => {
    if (isLoadingOlder || !hasOlder || messages.length === 0) return;
    const gen = loadGenRef.current;
    setIsLoadingOlder(true);
    try {
      const oldest = messages[0];
      const res = await apiRequest(
        "GET",
        buildUrl({ before: oldest.id, limit: pageSize }),
      );
      const data = await res.json();

      if (gen !== loadGenRef.current) return; // stale

      const olderMsgs: PaginatedMessage[] = data.messages ?? [];
      if (olderMsgs.length > 0) {
        setMessages((prev) => [...olderMsgs, ...prev]);
        // Shift unread anchor index to account for prepended messages
        setUnreadAnchorIndex((prev) =>
          prev !== null ? prev + olderMsgs.length : null,
        );
      }
      setHasOlder(data.hasOlder ?? false);
    } catch (err) {
      console.error("[usePaginatedMessages] loadOlder failed:", err);
    } finally {
      if (gen === loadGenRef.current) setIsLoadingOlder(false);
    }
  }, [isLoadingOlder, hasOlder, messages, pageSize, buildUrl]);

  // --- Poll for new messages ---
  // Use a ref to always have access to current messages without re-creating the interval
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const buildUrlRef = useRef(buildUrl);
  buildUrlRef.current = buildUrl;

  const fetchNewer = useCallback(async () => {
    const currentMessages = messagesRef.current;
    if (currentMessages.length === 0 || !initialLoadDone.current) return;
    const gen = loadGenRef.current;
    const newest = currentMessages[currentMessages.length - 1];
    try {
      const res = await apiRequest(
        "GET",
        buildUrlRef.current({ after: newest.id, limit: 50 }),
      );
      const data = await res.json();
      const newMsgs: PaginatedMessage[] = data.messages ?? [];
      if (newMsgs.length > 0 && gen === loadGenRef.current) {
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const unique = newMsgs.filter((m) => !existingIds.has(m.id));
          return unique.length > 0 ? [...prev, ...unique] : prev;
        });
      }
    } catch {
      // Silently ignore poll errors
    }
  }, []);

  useEffect(() => {
    if (!enabled || !initialLoadDone.current || pollInterval <= 0) return;
    pollTimerRef.current = setInterval(fetchNewer, pollInterval);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [enabled, spaceId, pollInterval, fetchNewer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  const appendMessage = useCallback((msg: PaginatedMessage) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === msg.id)) return prev;
      return [...prev, msg];
    });
  }, []);

  const updateMessage = useCallback(
    (id: string, updater: (msg: PaginatedMessage) => PaginatedMessage) => {
      setMessages((prev) => prev.map((m) => (m.id === id ? updater(m) : m)));
    },
    [],
  );

  return {
    messages,
    hasOlder,
    isLoadingInitial,
    isLoadingOlder,
    loadOlder,
    unreadAnchorIndex,
    appendMessage,
    updateMessage,
    pollNow: fetchNewer,
  };
}
